import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { AppButton } from '../../ui';
import chiffrageSiteService from '../../../services/chiffrageSiteService';

const KIND_ICON = {
  warn: ['lucide:triangle-alert', 'is-warn'],
  idea: ['lucide:lightbulb', 'is-idea'],
  upsell: ['lucide:trending-up', 'is-up'],
  info: ['lucide:info', 'is-info'],
};

const STEP_NAMES = ['Le site', 'Les contenus', 'Finitions'];

const formatMoney = (value) => `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;

let messageSeq = 0;
const nextId = () => {
  messageSeq += 1;
  return `m${Date.now()}-${messageSeq}`;
};

const ChiffrageAssistant = ({
  result,
  wip,
  variables,
  wizardStep = 0,
  dossierId,
  calculating = false,
  onApplyPatch,
  onClose,
}) => {
  const listRef = useRef(null);
  const rootRef = useRef(null);
  const applyPhase = useRef('idle');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [applyingKey, setApplyingKey] = useState(null);
  const [opinion, setOpinion] = useState(null);
  const [messages, setMessages] = useState(() => ([
    {
      id: 'welcome',
      role: 'assistant',
      text: 'Décrivez le projet en une phrase et je propose des réponses. Rien n’est modifié tant que vous n’appliquez pas. Je peux aussi expliquer une étape ou comparer avec vos chiffrages.',
    },
  ]));

  const hints = result?.assistant?.hints ?? [];
  const explanations = result?.assistant?.step_explanations ?? {};
  const warns = hints.filter((hint) => hint.kind === 'warn');
  const ideas = hints.filter((hint) => hint.kind !== 'warn');
  const stepExpl = explanations[wizardStep] || explanations[String(wizardStep)] || '';
  const blocked = result?.collaboration?.required === true;

  useEffect(() => {
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    const root = rootRef.current;
    const viewport = window.visualViewport;
    const query = window.matchMedia('(max-width: 991px)');
    let locked = false;
    let lockedY = 0;

    const unlockPage = () => {
      if (!locked) return;
      locked = false;
      const y = lockedY;
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, y);
    };

    const lockPage = () => {
      if (locked) return;
      locked = true;
      lockedY = window.scrollY || 0;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${lockedY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    };

    const clearSheetSize = () => {
      if (!root) return;
      root.style.height = '';
      root.style.top = '';
      root.style.bottom = '';
    };

    const sync = () => {
      if (!query.matches) {
        unlockPage();
        clearSheetSize();
        return;
      }
      lockPage();
      if (!root || !viewport) return;
      root.style.top = `${viewport.offsetTop}px`;
      root.style.height = `${viewport.height}px`;
      root.style.bottom = 'auto';
    };

    sync();
    viewport?.addEventListener('resize', sync);
    viewport?.addEventListener('scroll', sync);
    query.addEventListener('change', sync);
    return () => {
      viewport?.removeEventListener('resize', sync);
      viewport?.removeEventListener('scroll', sync);
      query.removeEventListener('change', sync);
      unlockPage();
      clearSheetSize();
    };
  }, []);

  const applyPatch = (patch, key) => {
    if (!patch || applyingKey) return;
    applyPhase.current = calculating ? 'wait-idle' : 'wait-start';
    setApplyingKey(key || 'patch');
    onApplyPatch?.(patch);
  };

  useEffect(() => {
    if (!applyingKey) return;
    if (applyPhase.current === 'wait-idle') {
      if (!calculating) applyPhase.current = 'wait-start';
      return;
    }
    if (applyPhase.current === 'wait-start' && calculating) {
      applyPhase.current = 'wait-end';
      return;
    }
    if (applyPhase.current === 'wait-end' && !calculating) {
      const key = applyingKey;
      setMessages((current) => current.map((message) => (
        message.id === key ? { ...message, applied: true } : message
      )));
      setApplyingKey(null);
      applyPhase.current = 'idle';
    }
  }, [calculating, applyingKey]);

  const push = (...items) => {
    setMessages((current) => [...current, ...items]);
  };

  const askOpinion = async (compare) => {
    const question = compare ? 'Compare avec mes chiffrages passés' : 'Ton avis sur le devis ?';
    push({ id: nextId(), role: 'user', text: question });
    setSending(true);
    try {
      const data = await chiffrageSiteService.askAssistant({
        mode: compare ? 'compare' : 'opinion',
        wip,
        variables,
        wizard_step: wizardStep,
        dossier_id: dossierId,
      });
      const nextOpinion = data.opinion;
      setOpinion(nextOpinion);
      push({
        id: nextId(),
        role: 'assistant',
        text: `${nextOpinion?.verdict || 'Avis'}. ${nextOpinion?.text || ''}`,
        peers: compare ? (nextOpinion?.peers ?? []) : [],
      });
    } catch (err) {
      push({ id: nextId(), role: 'assistant', text: err.message || 'Impossible de comparer pour le moment.' });
    } finally {
      setSending(false);
    }
  };

  const askLocal = (label, text, cards = []) => {
    push(
      { id: nextId(), role: 'user', text: label },
      { id: nextId(), role: 'assistant', text, cards },
    );
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    const history = messages
      .filter((message) => message.role === 'user' || message.role === 'assistant')
      .slice(-8)
      .map((message) => ({ role: message.role, content: message.text }));
    setDraft('');
    const userId = nextId();
    push({ id: userId, role: 'user', text });
    setSending(true);
    try {
      const reply = await chiffrageSiteService.askAssistant({
        mode: 'chat',
        message: text,
        history,
        wip,
        variables,
        wizard_step: wizardStep,
        dossier_id: dossierId,
      });
      push({
        id: nextId(),
        role: 'assistant',
        text: reply.text,
        proposals: reply.proposals ?? [],
        applied: false,
      });
    } catch (err) {
      push({ id: nextId(), role: 'assistant', text: err.message || 'Assistant indisponible.' });
    } finally {
      setSending(false);
    }
  };

  const chips = [
    {
      label: 'Qu’est-ce que j’oublie ?',
      go: () => askLocal(
        'Qu’est-ce que j’oublie ?',
        warns.length ? `${warns.length} point(s) à corriger avant d’envoyer :` : 'Rien d’incohérent. Le devis tient debout.',
        warns,
      ),
    },
    {
      label: 'Idées à proposer',
      go: () => askLocal(
        'Qu’est-ce que je pourrais proposer en plus ?',
        ideas.length ? 'Ce que je mettrais sur la table :' : 'Le devis est déjà complet, je n’ajouterais rien.',
        ideas,
      ),
    },
    {
      label: 'Explique cette étape',
      go: () => askLocal(`Explique-moi l’étape « ${STEP_NAMES[wizardStep]} »`, stepExpl || 'Cette étape rassemble les réponses affichées à gauche.'),
    },
    { label: 'Ton avis sur le devis', go: () => askOpinion(false) },
    { label: 'Comparer', go: () => askOpinion(true) },
  ];

  return (
    <aside className="chiffrage-assistant" ref={rootRef}>
      <div className="chiffrage-assistant__head">
        <span className="chiffrage-assistant__mark"><Icon icon="lucide:sparkles" width={17} /></span>
        <span className="chiffrage-assistant__titles">
          <strong>Assistant chiffrage</strong>
          <small>Conseils et propositions, rien n’est modifié sans votre accord</small>
        </span>
        <button type="button" className="chiffrage-icon-btn" aria-label="Fermer l’assistant" onClick={onClose}>
          <Icon icon="lucide:x" width={18} />
        </button>
      </div>
      <div className="chiffrage-assistant__strip">
        <span>
          {blocked ? 'Cadrage équipe' : (
            <>
              Création <strong>{formatMoney(result?.total_price)}</strong>
              {' · '}
              {formatMoney(result?.budget_n2)} / an
            </>
          )}
        </span>
        <button type="button" onClick={onClose}>
          Voir le détail
          <Icon icon="lucide:chevron-right" width={14} />
        </button>
      </div>
      <div className="chiffrage-assistant__scroll" ref={listRef}>
        <HintBlock title={`À corriger · ${warns.length}`} empty={warns.length === 0} emptyLabel="Rien d’incohérent pour l’instant." items={warns} onApply={applyPatch} applyingKey={applyingKey} />
        <HintBlock title={`Suggestions · ${ideas.length}`} items={ideas} onApply={applyPatch} applyingKey={applyingKey} />
        <section className="chiffrage-assistant__section">
          <span className="chiffrage-kicker">Cette étape · {STEP_NAMES[wizardStep]}</span>
          <p>{stepExpl}</p>
        </section>
        {opinion ? (
          <section className="chiffrage-assistant__opinion">
            <div className="chiffrage-assistant__opinion-head">
              <span className="chiffrage-kicker">Mon avis sur le devis</span>
              <b className={opinion.verdict_tone === 'ok' ? 'is-ok' : 'is-warn'}>{opinion.verdict}</b>
            </div>
            <p>{opinion.text}</p>
          </section>
        ) : null}
        {messages.map((message) => (
          <div key={message.id} className={`chiffrage-bubble-row${message.role === 'user' ? ' is-user' : ''}`}>
            <div className={`chiffrage-bubble${message.role === 'user' ? ' is-user' : ''}`}>
              <p>{message.text}</p>
              {(message.cards ?? []).map((card) => (
                <HintCard key={card.id} hint={card} onApply={applyPatch} applyingKey={applyingKey} />
              ))}
              {(message.peers ?? []).map((peer) => (
                <div key={`${peer.name}-${peer.sub}`} className="chiffrage-peer">
                  <span><strong>{peer.name}</strong><small>{peer.sub}</small></span>
                  <b>{formatMoney(peer.price)}</b>
                </div>
              ))}
              {message.proposals?.length ? (
                <div className="chiffrage-proposal">
                  {message.proposals.map((proposal) => (
                    <div key={proposal.key}>
                      <span>{proposal.label}</span>
                      <b>{proposal.display}</b>
                    </div>
                  ))}
                  {message.applied ? (
                    <span className="chiffrage-applied">Réponses appliquées au chiffrage</span>
                  ) : (
                    <AppButton
                      variant="secondary"
                      className="chiffrage-nav chiffrage-nav--dark"
                      loading={applyingKey === message.id}
                      loadingLabel="Application…"
                      disabled={Boolean(applyingKey)}
                      onClick={() => applyPatch(
                        Object.fromEntries(message.proposals.map((proposal) => [proposal.key, proposal.value])),
                        message.id,
                      )}
                    >
                      Appliquer ces {message.proposals.length} réponses
                    </AppButton>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ))}
        {sending ? <p className="chiffrage-assistant__wait">Réflexion…</p> : null}
      </div>
      <div className="chiffrage-assistant__composer">
        <div className="chiffrage-chips">
          {chips.map((chip) => (
            <button key={chip.label} type="button" disabled={sending} onClick={chip.go}>{chip.label}</button>
          ))}
        </div>
        <div className="chiffrage-composer">
          <input
            value={draft}
            placeholder="Décrivez le projet ou posez une question…"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') send();
            }}
          />
          <button type="button" aria-label="Envoyer" disabled={sending || !draft.trim()} onClick={send}>
            <Icon icon="lucide:arrow-up" width={18} />
          </button>
        </div>
      </div>
    </aside>
  );
};

const HintBlock = ({ title, items = [], empty = false, emptyLabel, onApply, applyingKey }) => (
  <section className="chiffrage-assistant__section">
    <span className="chiffrage-kicker">{title}</span>
    {items.map((hint) => (
      <HintCard key={hint.id} hint={hint} onApply={onApply} applyingKey={applyingKey} />
    ))}
    {empty ? <p className="chiffrage-assistant__ok"><Icon icon="lucide:circle-check" width={16} />{emptyLabel}</p> : null}
  </section>
);

const HintCard = ({ hint, onApply, applyingKey }) => {
  const [icon, tone] = KIND_ICON[hint.kind] || KIND_ICON.info;
  const loading = applyingKey === hint.id;
  return (
    <div className={`chiffrage-hint ${tone}`}>
      <div>
        <Icon icon={icon} width={16} />
        <span>{hint.text}</span>
      </div>
      <div className="chiffrage-hint__foot">
        <small>{[STEP_NAMES[hint.step], hint.impact].filter(Boolean).join(' · ')}</small>
        {hint.patch ? (
          <AppButton
            variant="secondary"
            className="chiffrage-apply"
            loading={loading}
            loadingLabel="Application…"
            disabled={Boolean(applyingKey)}
            onClick={() => onApply(hint.patch, hint.id)}
          >
            Appliquer
          </AppButton>
        ) : null}
      </div>
    </div>
  );
};

export default ChiffrageAssistant;
